import React, { useMemo, useState } from 'react';
import type { CalendarContextSummary, CalendarContextSummaryDay } from '../../types/messages';

type Props = {
  text: string;
  summary: CalendarContextSummary;
  onSelectDate?: (dateText: string) => void;
};

const weekdayFormatter = new Intl.DateTimeFormat('ja-JP', { weekday: 'short' });

const formatCalendarDate = (dateText: string) => {
  const [year, month, day] = dateText.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return `${month}/${day}(${weekdayFormatter.format(date)})`;
};

const formatPeriod = (from: string, to: string) => {
  return `${formatCalendarDate(from)} - ${formatCalendarDate(to)}`;
};

const formatDaySummary = (timedEventsCount: number, allDayBlockers: string[]) => {
  if (allDayBlockers.length > 0) {
    return '終日予定あり';
  }

  if (timedEventsCount === 0) {
    return '予定なし';
  }

  return `${timedEventsCount}件の予定`;
};

const feasibilityLabelMap = {
  possible: '提案しやすい',
  possible_with_buffer: '移動時間に注意',
  possible_with_adjustment: '調整余地あり',
  difficult: '提案しづらい'
} as const;

const blockerLabelMap = {
  low: '制約低',
  medium: '制約中',
  high: '制約高'
} as const;

const PRIMARY_FEASIBILITIES = ['possible', 'possible_with_buffer'] as const;

const isPrimaryFeasibility = (
  proposalFeasibility: CalendarContextSummaryDay['proposalFeasibility']
): proposalFeasibility is typeof PRIMARY_FEASIBILITIES[number] => {
  return proposalFeasibility === 'possible' || proposalFeasibility === 'possible_with_buffer';
};

const formatMinutes = (minutes: number) => {
  if (minutes === 0) {
    return '0分';
  }

  const hours = Math.floor(minutes / 60);
  const remainMinutes = minutes % 60;

  if (hours === 0) {
    return `${remainMinutes}分`;
  }

  if (remainMinutes === 0) {
    return `${hours}時間`;
  }

  return `${hours}時間${remainMinutes}分`;
};

const CalendarContextSummaryMessage = ({ text, summary, onSelectDate }: Props) => {
  const primaryDates = useMemo(() => {
    const datesFromInterpretation = summary.days
      .filter(day => isPrimaryFeasibility(day.proposalFeasibility))
      .map(day => day.date);

    if (datesFromInterpretation.length > 0) {
      return datesFromInterpretation;
    }

    if (summary.candidateDates.length > 0) {
      return summary.candidateDates;
    }

    return summary.suggestedDates.slice(0, 2);
  }, [summary.days, summary.candidateDates, summary.suggestedDates]);
  const initialExpandedDates = useMemo(() => new Set(primaryDates), [primaryDates]);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(initialExpandedDates);

  const toggleDate = (date: string) => {
    setExpandedDates(prev => {
      const next = new Set(prev);
      if (next.has(date)) {
        next.delete(date);
      } else {
        next.add(date);
      }
      return next;
    });
  };

  const expandDate = (date: string) => {
    setExpandedDates(prev => {
      if (prev.has(date)) {
        return prev;
      }

      const next = new Set(prev);
      next.add(date);
      return next;
    });
  };

  return (
    <div style={styles.card}>
      <div style={styles.lead}>{text}</div>
      <div style={styles.periodSection}>
        <div style={styles.periodLabel}>{summary.period.label}</div>
        <div style={styles.periodRange}>{formatPeriod(summary.period.from, summary.period.to)}</div>
        {summary.overallSummary && <div style={styles.overallSummary}>{summary.overallSummary}</div>}
      </div>
      {primaryDates.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>確認候補</div>
          <div style={styles.chipRow}>
            {primaryDates.map(date => (
              <button key={date} type="button" style={styles.dateChipButton} onClick={() => expandDate(date)}>
                <span style={styles.dateChip}>{formatCalendarDate(date)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>日ごとの予定状況</div>
        <div style={styles.dayList}>
          {summary.days.map(day => {
            const isExpanded = expandedDates.has(day.date);
            const isSuggested = primaryDates.includes(day.date);

            return (
              <div key={day.date} style={styles.dayAccordion}>
                <button
                  type="button"
                  style={{
                    ...styles.daySummaryButton,
                    ...(isExpanded ? styles.daySummaryButtonExpanded : {})
                  }}
                  onClick={() => toggleDate(day.date)}
                >
                  <div style={styles.dayHeader}>
                    <div style={styles.dayTitleRow}>
                      <div style={styles.dayTitle}>{formatCalendarDate(day.date)}</div>
                      {isSuggested && <span style={styles.recommendedBadge}>候補</span>}
                    </div>
                    <div style={styles.dayHeaderRight}>
                      <div style={styles.dayStatus}>
                        {formatDaySummary(day.timedEventsCount, day.allDayBlockers)}
                      </div>
                      <span style={styles.expandIcon}>{isExpanded ? '−' : '+'}</span>
                    </div>
                  </div>
                  {(day.proposalFeasibility || day.blockerLevel) && (
                    <div style={styles.labelRow}>
                      {day.proposalFeasibility && (
                        <span style={styles.feasibilityChip}>{feasibilityLabelMap[day.proposalFeasibility]}</span>
                      )}
                      {day.blockerLevel && (
                        <span style={styles.blockerLevelChip}>{blockerLabelMap[day.blockerLevel]}</span>
                      )}
                    </div>
                  )}
                  {isExpanded && day.reasonSummary && <div style={styles.reasonSummary}>{day.reasonSummary}</div>}
                </button>
                {isExpanded && (
                  <div style={styles.dayCard}>
                    <div style={styles.metricRow}>
                      <div style={styles.metric}>
                        <div style={styles.metricLabel}>予定件数</div>
                        <div style={styles.metricValue}>{day.timedEventsCount}件</div>
                      </div>
                      <div style={styles.metric}>
                        <div style={styles.metricLabel}>予定時間</div>
                        <div style={styles.metricValue}>{formatMinutes(day.totalBusyMinutes)}</div>
                      </div>
                      <div style={styles.metric}>
                        <div style={styles.metricLabel}>最大空き時間</div>
                        <div style={styles.metricValue}>{formatMinutes(day.longestFreeSlotMinutes)}</div>
                      </div>
                    </div>
                    {day.allDayBlockers.length > 0 && (
                      <div style={styles.detailBlock}>
                        <div style={styles.detailTitle}>終日予定</div>
                        <div style={styles.blockerList}>
                          {day.allDayBlockers.map(blocker => (
                            <span key={blocker} style={styles.blockerChip}>
                              {blocker}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {day.busySummaries.length > 0 && (
                      <div style={styles.detailBlock}>
                        <div style={styles.detailTitle}>予定詳細</div>
                        <ul style={styles.summaryList}>
                          {day.busySummaries.map(summaryText => (
                            <li key={summaryText} style={styles.summaryItem}>
                              {summaryText}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {day.allDayBlockers.length === 0 && day.busySummaries.length === 0 && (
                      <div style={styles.emptyState}>この日の予定は確認できませんでした。</div>
                    )}
                    {onSelectDate && (
                      <div style={styles.actionRow}>
                        <button
                          type="button"
                          style={styles.selectDateButton}
                          onClick={() => onSelectDate(day.date)}
                        >
                          この日で提案してもらう
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const styles = {
  card: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
    padding: '16px',
    borderRadius: '16px',
    border: '1px solid #d9e4ef',
    backgroundColor: '#f7fbff',
    boxShadow: '0 8px 24px rgba(20, 55, 90, 0.08)',
    minWidth: 0
  },
  lead: {
    color: '#35526d',
    fontSize: '14px',
    lineHeight: 1.7,
    overflowWrap: 'break-word' as const
  },
  periodSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
    padding: '12px 14px',
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    border: '1px solid #d8e4ee'
  },
  periodLabel: {
    color: '#11324d',
    fontSize: '14px',
    fontWeight: 700
  },
  periodRange: {
    color: '#4f6f89',
    fontSize: '13px'
  },
  overallSummary: {
    color: '#23435c',
    fontSize: '13px',
    lineHeight: 1.6,
    marginTop: '4px',
    overflowWrap: 'break-word' as const
  },
  section: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '10px'
  },
  sectionTitle: {
    color: '#35526d',
    fontSize: '13px',
    fontWeight: 700
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px'
  },
  dateChipButton: {
    padding: 0,
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer'
  },
  dateChip: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 10px',
    borderRadius: '999px',
    backgroundColor: '#e7f2fb',
    color: '#11324d',
    fontSize: '12px',
    fontWeight: 600
  },
  dayList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '10px'
  },
  dayAccordion: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px'
  },
  daySummaryButton: {
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    textAlign: 'left' as const,
    width: '100%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '10px',
    padding: '14px',
    borderRadius: '12px',
    border: '1px solid #d8e4ee',
    whiteSpace: 'normal' as const,
    overflowWrap: 'anywhere' as const,
    wordBreak: 'break-word' as const,
    overflow: 'hidden' as const
  },
  daySummaryButtonExpanded: {
    borderColor: '#b8d0e5'
  },
  dayCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
    padding: '0 14px 14px',
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    border: '1px solid #d8e4ee',
    minWidth: 0,
    overflowWrap: 'anywhere' as const,
    wordBreak: 'break-word' as const
  },
  dayHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: '12px'
  },
  dayHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  dayTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0
  },
  dayTitle: {
    color: '#11324d',
    fontSize: '15px',
    fontWeight: 700
  },
  recommendedBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    borderRadius: '999px',
    backgroundColor: '#dff0ff',
    color: '#0f588c',
    fontSize: '11px',
    fontWeight: 700
  },
  dayStatus: {
    color: '#5d7690',
    fontSize: '12px',
    fontWeight: 600
  },
  expandIcon: {
    color: '#6c859a',
    fontSize: '18px',
    fontWeight: 700,
    lineHeight: 1
  },
  labelRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px'
  },
  feasibilityChip: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 8px',
    borderRadius: '999px',
    backgroundColor: '#e7f4ea',
    color: '#1f6b39',
    fontSize: '11px',
    fontWeight: 700
  },
  blockerLevelChip: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 8px',
    borderRadius: '999px',
    backgroundColor: '#eef3f8',
    color: '#4d647c',
    fontSize: '11px',
    fontWeight: 700
  },
  reasonSummary: {
    color: '#23435c',
    fontSize: '13px',
    lineHeight: 1.7,
    whiteSpace: 'normal' as const,
    overflowWrap: 'anywhere' as const,
    wordBreak: 'break-word' as const
  },
  metricRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: '8px'
  },
  metric: {
    padding: '10px 12px',
    borderRadius: '10px',
    backgroundColor: '#f6f9fc',
    border: '1px solid #e4edf5'
  },
  metricLabel: {
    color: '#6b8195',
    fontSize: '11px',
    marginBottom: '4px'
  },
  metricValue: {
    color: '#11324d',
    fontSize: '13px',
    fontWeight: 700
  },
  detailBlock: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    minWidth: 0
  },
  detailTitle: {
    color: '#35526d',
    fontSize: '12px',
    fontWeight: 700
  },
  blockerList: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px'
  },
  blockerChip: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 10px',
    borderRadius: '999px',
    backgroundColor: '#fff5e5',
    color: '#8a5a00',
    fontSize: '12px',
    fontWeight: 600,
    wordBreak: 'break-all' as const
  },
  summaryList: {
    margin: 0,
    paddingLeft: '18px',
    color: '#35526d',
    fontSize: '13px',
    lineHeight: 1.7,
    wordBreak: 'break-word' as const
  },
  summaryItem: {
    marginBottom: '4px',
    overflowWrap: 'anywhere' as const
  },
  emptyState: {
    color: '#5d7690',
    fontSize: '13px',
    lineHeight: 1.6
  },
  actionRow: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  selectDateButton: {
    border: 'none',
    borderRadius: '999px',
    padding: '8px 12px',
    backgroundColor: '#0f588c',
    color: '#ffffff',
    fontSize: '12px',
    fontWeight: 700,
    cursor: 'pointer'
  }
};

export default CalendarContextSummaryMessage;
